import { useMemo } from 'react';
import {
  Box,
  CardRow,
  Grid,
  Sticker,
  TextButton,
  Typography,
  useTheme,
  styled,
  useMediaQuery,
} from 'bonfire-kindling';
import { useTranslation } from 'react-i18next';

import { getLocations } from 'helpers/locations';
import { utcToTimezone } from 'helpers/dates';
import { TimeRemaining } from 'components';
import { useProjectClosingDate } from '../../hooks/useProjectClosingDate';

const StyledProjectContentContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'stretch',
  flexDirection: 'column',
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    alignItems: 'center',
    flexDirection: 'row',
    marginTop: theme.spacing(2),
    '& > *:not(:last-child)': { marginRight: theme.spacing(3) },
  },
}));
export default function BasicOpportunityCard({
  Locations,
  DateOpen,
  DateClose,
  ProjectName,
  onClick,
}) {
  const { t } = useTranslation();
  const { palette } = useTheme();
  const { isOpenProject, isClosingSoon, remaining } = useProjectClosingDate(DateClose);
  const isMobileWidth = useMediaQuery(theme => theme.breakpoints.down('sm'));

  const { summary: locationsSummary } = useMemo(() => getLocations(Locations), [Locations]);
  const timeRemaining = isOpenProject && (
    <TimeRemaining
      label={
        isClosingSoon
          ? t('opportunities.project.time_remaining')
          : t('opportunities.project.days_left', { count: remaining.days })
      }
      value={isClosingSoon ? remaining.hours : remaining.days}
    />
  );
  const statusSticker = (
    <Box>
      <Sticker background={isOpenProject ? palette.green.dark : palette.grey.light}>
        {isOpenProject ? t('opportunities.project.open') : t('opportunities.project.closed')}
      </Sticker>
    </Box>
  );
  return (
    <Box m={2}>
      <CardRow>
        <Box p={3}>
          {!isMobileWidth && <Typography variant="h4">{ProjectName}</Typography>}
          <StyledProjectContentContainer>
            {isMobileWidth ? (
              <>
                <Box display="flex">
                  <Box mr="auto">
                    <Typography variant="h4" gutterBottom>
                      {ProjectName}
                    </Typography>
                    {statusSticker}
                    <Typography variant="body2" my={1}>
                      {locationsSummary}
                    </Typography>
                  </Box>
                  {timeRemaining}
                </Box>
              </>
            ) : (
              <>
                {statusSticker}
                <Typography variant="body2" sx={{ width: '15%' }}>
                  {locationsSummary}
                </Typography>
                {timeRemaining}
              </>
            )}
            <Typography variant="body3" sx={{ flexGrow: 1 }}>
              <Grid container spacing={1}>
                <Grid item xs={4} md={2}>
                  <strong>{t('opportunities.project.open_date')}</strong>
                </Grid>
                <Grid item xs={8} md={4}>
                  {utcToTimezone(DateOpen)}
                </Grid>
                <Grid item xs={4} md={2}>
                  <strong>{t('opportunities.project.close_date')}</strong>
                </Grid>
                <Grid item xs={8} md={4}>
                  {utcToTimezone(DateClose)}
                </Grid>
              </Grid>
            </Typography>
            <TextButton
              label={t('opportunities.project.go_to_project')}
              variant="outlined"
              onClick={onClick}
              sx={{ flex: 'none', marginLeft: 'auto', mt: { xs: 2, sm: 0 } }}
            />
          </StyledProjectContentContainer>
        </Box>
      </CardRow>
    </Box>
  );
}
